// frontend/src/native/ota.ts
//
// OTA-Kern (Phase 0) — manueller, selbst-gehosteter Capgo-Updater-Wrapper.
//
// ARCHITEKTUR (fest, siehe IMPLEMENTATION CONTRACT):
//   - Plugin: @capgo/capacitor-updater im MANUAL/SELF-HOSTED-Modus
//     (capacitor.config: plugins.CapacitorUpdater.autoUpdate = false). Wir steuern
//     download()/set() selbst und treiben die Vordergrund-Progress-UI.
//   - Selbst gehostet auf UNSEREM Server. Kein Capgo-Cloud, kein Supabase.
//   - Boot-Reihenfolge: notifyAppReady() ist das ALLERERSTE (entschärft den
//     appReadyTimeout-Auto-Rollback für das aktuell laufende, gesunde Bundle),
//     DANN check → download → set (set lädt die WebView neu, terminal).
//   - KEIN "Später"/Skip: bei verfügbarem Update blockiert das Overlay bis es
//     angewendet ist. ABER bei Download-/Netzfehler wird NICHT gebrickt — wir
//     loggen und laufen auf dem AKTUELLEN Bundle weiter ({updated:false}),
//     nächster Start versucht es erneut.
//   - Web (nicht-nativ): die gesamte OTA-Schicht ist ein No-Op (Guard wie bridge.ts).
//
// Manueller Boot-Flow (Capgo v8, verifizierte API):
//   notifyAppReady() → download({url, version}) → set({ id: result.id }) (reload)
//   Auf dem nächsten Start muss das JS des neuen Bundles notifyAppReady() erneut
//   aufrufen, sonst rollt der native Layer zurück.

// STATISCHE Imports (KEINE dynamischen import()s im OTA-Pfad): @capacitor/core +
// @capacitor/app sind ohnehin app-weit eingebunden und werden mit dem Haupt-Bundle
// EAGER geladen. Lazy-`import()` von Plugin-Chunks hing im Phase-0-Boot unter
// capacitor:// (Builds 70035/70037) → komplett vermieden. Den OTA-Plugin-Proxy
// holen wir über registerPlugin('CapacitorUpdater') (nativ; kein @capgo-JS nötig).
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { APP_VERSION } from './appVersion';
import {
  type AppEnv,
  apiBaseForEnv,
  clearEnvForce,
  clearEnvOverride,
  hydrateEnvState,
  setEnvForce,
  writeEnvOverride,
} from '../env/appEnv';

// ─── Typen ───────────────────────────────────────────────────────────────────

export type OtaChannel = 'production' | 'beta';
export type OtaPlatform = 'ios' | 'android';

/** Fortschritts-Kontrakt zwischen ota-core und ui (feste Form). */
export interface OtaProgress {
  phase: 'bundle';
  /** 0..100 */
  percent: number;
  bytesDone?: number;
  bytesTotal?: number;
}

export type OtaProgressCallback = (progress: OtaProgress) => void;

/** Rückgabewert von runOtaUpdate. Bei updated:true ruft set() vorher reload() auf
 *  → der Aufrufer sieht diesen Wert in der Praxis nur im updated:false-Fall. */
export interface OtaResult {
  updated: boolean;
  error?: unknown;
}

/** Antwort von GET /api/ota/check (unser Server-Protokoll). */
export interface OtaCheckResponse {
  available: boolean;
  version?: string;
  url?: string;
  checksum?: string;
  minNativeVersionCode?: number;
}

// Minimal getypte Sicht auf das Capgo-Plugin-API (lokal deklariert, damit das
// Modul auch kompiliert/lädt, bevor das npm-Paket installiert ist und ohne von
// dessen Typen abzuhängen). Namen exakt aus @capgo/capacitor-updater v8.49.7.
interface CapgoBundleInfo {
  id: string;
  version: string;
  downloaded: string;
  checksum: string;
  status: string;
}
interface CapgoDownloadEvent {
  percent: number;
  bundle: CapgoBundleInfo;
}
interface CapgoListenerHandle {
  remove: () => Promise<void> | void;
}
interface CapacitorUpdaterPlugin {
  notifyAppReady: () => Promise<{ bundle: CapgoBundleInfo }>;
  download: (options: {
    url: string;
    version: string;
    sessionKey?: string;
    checksum?: string;
  }) => Promise<CapgoBundleInfo>;
  set: (options: { id: string }) => Promise<void>;
  reload: () => Promise<void>;
  /** R6-02-b: Rueckweg auf das EINGEBAUTE Store-Bundle. Im Plugin vorhanden
   *  (v8.49.7), fehlte hier bisher nur in der Typsicht. Optional deklariert,
   *  damit ein aelterer nativer Build ohne die Methode nicht hart bricht. */
  reset?: (options?: { toLastSuccessful?: boolean }) => Promise<void>;
  addListener: (
    eventName: 'download',
    listenerFunc: (state: CapgoDownloadEvent) => void,
  ) => Promise<CapgoListenerHandle>;
}

// ─── Konstanten ──────────────────────────────────────────────────────────────

const DEFAULT_CHANNEL: OtaChannel = 'production';
/** Timeout für den /api/ota/check-Request. Großzügig, aber endlich (Boot-Pfad). */
const CHECK_TIMEOUT_MS = 12000;

// ─── Reine Logik (test-isoliert) ─────────────────────────────────────────────

/**
 * Liest den OTA-Channel aus dem Build-Env (VITE_OTA_CHANNEL). Default 'production'.
 * Beta/Prod sind strikt getrennt — der Channel wird explizit an den Server
 * geschickt und dort validiert (Defense in Depth: Server-Manifest ist je Channel).
 */
export function resolveOtaChannel(raw: string | undefined | null): OtaChannel {
  return raw === 'beta' ? 'beta' : DEFAULT_CHANNEL;
}

/**
 * R6-02-b: Wohin geht der OTA-Lauf? Reine Funktion, damit sie ohne Capacitor
 * testbar bleibt.
 *   - Wunsch 'beta'       → beta.lorath.eu, Kanal beta
 *   - Wunsch 'production' → app.lorath.eu, Kanal production
 *   - kein Wunsch         → die eingebackenen Bauzeit-Werte
 *
 * Warum der Host MITWECHSELN muss: das OTA-Manifest liegt je Umgebung auf dem
 * eigenen Volume (prod ./data, beta ./data-beta). Der Prod-Host kennt den
 * Beta-Kanal nur veraltet — ein Umschalter, der nur den Kanal setzt und den
 * Host laesst, tut stillschweigend gar nichts (Diagnose C2-R6-02, gemessen).
 */
export function resolveOtaTarget(params: {
  override?: AppEnv | null;
  buildApiBase?: string | null;
  buildChannel?: string | null;
}): { apiBase: string; channel: OtaChannel } {
  const override = params.override ?? null;
  if (override === 'beta') return { apiBase: apiBaseForEnv('beta'), channel: 'beta' };
  if (override === 'production') return { apiBase: apiBaseForEnv('production'), channel: 'production' };
  return {
    apiBase: (params.buildApiBase ?? '').replace(/\/+$/, ''),
    channel: resolveOtaChannel(params.buildChannel),
  };
}

/**
 * Baut die /api/ota/check-URL. `apiBase` wird vorangestellt (im nativen Build
 * gesetzt: app.lorath.eu bzw. beta.lorath.eu — die strikte Beta/Prod-Trennung
 * auf Achse (a)). Trailing-Slashes werden normalisiert.
 */
export function buildCheckUrl(params: {
  apiBase: string | undefined | null;
  platform: OtaPlatform;
  versionCode: number;
  bundle: string;
  channel: OtaChannel;
}): string {
  const base = (params.apiBase ?? '').replace(/\/+$/, '');
  const query = new URLSearchParams({
    platform: params.platform,
    versionCode: String(params.versionCode),
    bundle: params.bundle,
    channel: params.channel,
  });
  return `${base}/api/ota/check?${query.toString()}`;
}

/**
 * Defense-in-Depth-Client-Entscheidung: Soll das gemeldete Update angewendet
 * werden? Der Server gated bereits (newestes Bundle des Channels mit
 * minNativeVersionCode <= versionCode UND version != aktuelles bundle), aber wir
 * prüfen erneut, damit ein fehlkonfigurierter Server den Client nie auf ein
 * inkompatibles oder identisches Bundle zwingt.
 *
 * Liefert die anzuwendende {url, version, checksum?} ODER null (überspringen).
 */
// F91 (Sicherheits-Audit 21.10.2026): Bundles nur von den eigenen Hosts und
// nur mit sha256-Prüfsumme (Capgo prüft sie beim Download). Eine echte Signatur
// (ed25519) folgt separat. Greift erst mit dem nächsten Bundle/Store-Build.
export const OTA_ALLOWED_HOSTS: readonly string[] = ['app.lorath.eu', 'beta.lorath.eu'];
const OTA_CHECKSUM_RE = /^[a-f0-9]{64}$/i;

export function isAllowedOtaUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  if (parsed.username || parsed.password || parsed.port) return false;
  return OTA_ALLOWED_HOSTS.includes(parsed.hostname.toLowerCase());
}

export function decideOtaApply(args: {
  response: OtaCheckResponse | null | undefined;
  versionCode: number;
  currentBundle: string;
}): { url: string; version: string; checksum: string } | null {
  const res = args.response;
  if (!res || res.available !== true) return null;

  const url = res.url;
  const version = res.version;
  // Ohne URL+Version ist die Antwort unbrauchbar.
  if (typeof url !== 'string' || url.length === 0) return null;
  if (typeof version !== 'string' || version.length === 0) return null;

  // HTTPS-Pflicht: iOS ATS + Android-Cleartext blocken plain HTTP ohnehin →
  // gar nicht erst versuchen (würde nur in einen Download-Fehler laufen).
  if (!/^https:\/\//i.test(url)) return null;
  // F91: Host-Pinning — nur eigene Hosts, keine Zugangsdaten/Ports in der URL.
  if (!isAllowedOtaUrl(url)) return null;
  // F91: Prüfsumme ist Pflicht.
  const checksum = typeof res.checksum === 'string' ? res.checksum.trim() : '';
  if (!OTA_CHECKSUM_RE.test(checksum)) return null;

  // Identisches Bundle nie erneut anwenden (verhindert Reload-Schleife, falls
  // der Server den eigenen Stand zurückmeldet).
  if (version === args.currentBundle) return null;

  // Native-Mindest-Build respektieren: ein Bundle, das einen neueren nativen
  // Build voraussetzt, darf NICHT auf einen älteren App-Build geladen werden.
  const minNative = res.minNativeVersionCode;
  if (typeof minNative === 'number' && Number.isFinite(minNative)) {
    if (args.versionCode < minNative) return null;
  }

  return { url, version, checksum: checksum.toLowerCase() };
}

// ─── Plugin-Laden (SYNCHRON, statisch — kein dynamischer Import) ─────────────

/**
 * Holt den nativen CapacitorUpdater-Plugin-Proxy — VOLLSTÄNDIG SYNCHRON, ohne
 * jeden `import()`. registerPlugin/Capacitor sind statisch importiert (mit dem
 * Haupt-Bundle geladen). KEIN @capgo-JS-Modul (die OTA-Arbeit ist nativ). Damit
 * kann hier NICHTS mehr hängen (Lazy-Chunk-Hänger der Builds 70035/70037 entfällt).
 */
function loadUpdaterPlugin(): CapacitorUpdaterPlugin | null {
  try {
    if (Capacitor?.isPluginAvailable?.('CapacitorUpdater') !== true) return null;
    return registerPlugin<CapacitorUpdaterPlugin>('CapacitorUpdater');
  } catch {
    return null;
  }
}

async function getNativeVersionCode(): Promise<number | null> {
  try {
    const info = await App.getInfo(); // App ist statisch importiert
    const code = Number.parseInt(String(info.build), 10);
    return Number.isFinite(code) ? code : null;
  } catch {
    return null;
  }
}

// 6.1: Versionsinfo für die Banner-Versionserkennung. nativeVersionCode = die
// installierte native Build-Nummer (null = Web/PWA, kein natives Gerät),
// bundleVersion = die aktuell laufende Web-Bundle-Version (APP_VERSION).
export async function getInstalledVersionInfo(): Promise<{ nativeVersionCode: number | null; bundleVersion: string }> {
  const isNative = Capacitor?.isNativePlatform?.() === true;
  return {
    nativeVersionCode: isNative ? await getNativeVersionCode() : null,
    bundleVersion: APP_VERSION,
  };
}

function getNativePlatform(): OtaPlatform | null {
  const platform = Capacitor?.getPlatform?.(); // statisch importiert
  if (platform === 'ios' || platform === 'android') return platform;
  return null;
}

// ─── Netzwerk: /api/ota/check ────────────────────────────────────────────────

async function fetchOtaCheck(url: string): Promise<OtaCheckResponse | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  try {
    // PUBLIC-Endpoint (läuft vor Login), kein Bearer/Cookie nötig → credentials:'omit'.
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as OtaCheckResponse;
    return data && typeof data === 'object' ? data : null;
  } catch {
    // Offline / Timeout / abgebrochen → kein Update (nicht bricken).
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Öffentlicher Einstieg: runOtaUpdate ─────────────────────────────────────

/**
 * Phase-0-OTA-Lauf. ROBUST + instrumentiert (nach dem 70036-Hang).
 *  - notifyAppReady() wird NICHT awaited (fire-and-forget): auf manchen Geräten
 *    löst das JS-Promise nie auf, OBWOHL die native Seite "ready" markiert. Ein
 *    `await` würde den ganzen Lauf blockieren → Nutzer hängt im no-skip-Overlay
 *    bei 0 % (genau der 70036-Bug). Der native Aufruf wird trotzdem abgesetzt
 *    und entschärft den Auto-Rollback.
 *  - JEDER native Aufruf hat ein Timeout (getInfo, download); fetchOtaCheck hat
 *    seins bereits. So kann KEIN Schritt den Boot-Pfad unendlich blockieren.
 *  - Beacons (GET /api/ota/check?…&beacon=STEP, server-geloggt) machen den
 *    Fortschritt ON-DEVICE in den Apache-Access-Logs sichtbar — Ferndiagnose
 *    ohne Gerätelog (der letzte Beacon vor Stille = Hänger-Stelle).
 *  - Jeder Fehler/Timeout → {updated:false} (nie werfen, nie blockieren).
 * Web/nicht-nativ: sofort {updated:false} (No-Op).
 */
export async function runOtaUpdate(onProgress: OtaProgressCallback): Promise<OtaResult> {
  // Guard: auf Web/PWA passiert nichts.
  if (!Capacitor?.isNativePlatform?.()) {
    return { updated: false };
  }
  const buildApiBase = import.meta.env.VITE_API_BASE;
  // Der allererste Beacon geht bewusst noch an die EINGEBACKENE Basis: er ist
  // das Ferndiagnose-Signal „JS lebt" und darf nicht hinter dem Lesen des
  // Umgebungsspeichers warten.
  beacon('start', { apiBase: buildApiBase });

  // R6-02-b: Umgebungswunsch lesen (nativer Speicher hat Vorrang und wird in
  // die synchronen Speicher zurueckgespiegelt). Mit Timeout, damit ein
  // haengendes Preferences-Plugin den Boot-Pfad nicht blockiert.
  const envState = await withTimeout(hydrateEnvState(), 6000, 'envState');
  const override = envState?.env ?? null;
  const forceFirstCheck = envState?.force === true;
  const target = resolveOtaTarget({
    override,
    buildApiBase,
    buildChannel: import.meta.env.VITE_OTA_CHANNEL as string | undefined,
  });
  const apiBase = target.apiBase;

  const updater = loadUpdaterPlugin(); // SYNCHRON — kein await, kann nicht hängen
  if (!updater) {
    beacon('no_plugin', { apiBase });
    return { updated: false };
  }
  beacon('plugin_ok', { apiBase });

  // ── notifyAppReady() FIRE-AND-FORGET (NICHT awaiten) ──
  try {
    void updater
      .notifyAppReady()
      .then(() => beacon('notify_ok', { apiBase }))
      .catch((error) => {
        logOta('notifyAppReady failed', error);
        beacon('notify_err', { apiBase });
      });
  } catch (error) {
    logOta('notifyAppReady threw synchronously', error);
  }
  beacon('notify_fired', { apiBase });

  let listener: CapgoListenerHandle | null = null;
  try {
    // ── check (versionCode mit Timeout, damit getInfo nicht hängen kann) ──
    const platform = getNativePlatform();
    const versionCode = await withTimeout(getNativeVersionCode(), 6000, 'getInfo');
    if (!platform || versionCode === null) {
      beacon('no_versioncode', { apiBase, platform: platform ?? undefined });
      return { updated: false };
    }
    beacon('versioncode_ok', { apiBase, platform, versionCode });

    const channel = target.channel;
    // R6-02-b: Beim ERSTEN Check nach dem Umschalten geht `bundle=''` an den
    // Server. Sonst greift dessen Downgrade-Sperre („muss echt neuer sein",
    // server/repositories/ota-repository.js:137-139) und der Wechsel taete
    // stillschweigend gar nichts, sobald die Zielumgebung niedriger nummeriert
    // ist. Der Client-Gate decideOtaApply bekommt denselben Wert, damit beide
    // Seiten dieselbe Frage beantworten.
    const currentBundle = forceFirstCheck ? '' : APP_VERSION;
    const checkUrl = buildCheckUrl({ apiBase, platform, versionCode, bundle: currentBundle, channel });

    const response = await fetchOtaCheck(checkUrl);
    // Das Kennzeichen ist EINMALIG: sobald der Server geantwortet hat, ist der
    // erzwungene Check verbraucht — sonst laedt das Geraet bei jedem Start neu.
    // Bei `null` (offline/Timeout) bleibt es stehen, damit der naechste Start
    // den Wechsel noch einmal ernsthaft versuchen kann.
    if (forceFirstCheck && response !== null) {
      await withTimeout(clearEnvForce(), 5000, 'clearEnvForce');
    }
    const apply = decideOtaApply({ response, versionCode, currentBundle });
    if (!apply) {
      beacon('no_update', { apiBase, platform, versionCode, bundle: APP_VERSION, channel });
      return { updated: false };
    }
    beacon('will_download', { apiBase, platform, versionCode, bundle: APP_VERSION, channel });

    // ── download (mit Progress + Timeout) ──
    listener = await updater.addListener('download', (state: CapgoDownloadEvent) => {
      try {
        onProgress({ phase: 'bundle', percent: clampPercent(state?.percent) });
      } catch {
        // Eine fehlerhafte UI-Callback darf den Download nicht stören.
      }
    });
    try { onProgress({ phase: 'bundle', percent: 0 }); } catch { /* ignorieren */ }

    const downloaded = await withTimeout(
      updater.download({
        url: apply.url,
        version: apply.version,
        checksum: apply.checksum,
      }),
      120000,
      'download',
    );
    if (!downloaded || typeof downloaded.id !== 'string' || downloaded.id.length === 0) {
      beacon('download_failed', { apiBase, platform, versionCode });
      return { updated: false };
    }
    beacon('download_ok', { apiBase, platform, versionCode });

    // Listener entfernen BEVOR set() den JS-Kontext zerstört.
    await removeListener(listener);
    listener = null;
    try { onProgress({ phase: 'bundle', percent: 100 }); } catch { /* ignorieren */ }

    // ── set() — TERMINAL (zerstört JS-Kontext + reload). reload() als Sicherheitsnetz. ──
    // FIX 04-P1-3: set()/reload() mit Timeout kapseln. Im Normalfall zerstört set()
    // den JS-Kontext (die WebView lädt neu), das `await` kehrt nie zurück — der
    // Timeout ist dann irrelevant. Hängt der native Aufruf aber (Race mit Listenern /
    // nativer Fehler ohne Reload), blieb runOtaUpdate sonst UNENDLICH offen → der
    // Aufrufer hing im OTA-Overlay. Nach der Frist erzwingen wir reload() als Fallback.
    await withTimeout(updater.set({ id: downloaded.id }), 30000, 'set').catch(() => null);
    try { await withTimeout(updater.reload(), 10000, 'reload'); } catch { /* set() hat i.d.R. bereits reloadt */ }
    return { updated: true };
  } catch (error) {
    logOta('runOtaUpdate failed', error);
    beacon('run_error', { apiBase });
    return { updated: false, error };
  } finally {
    if (listener) {
      await removeListener(listener);
    }
  }
}

// ─── R6-02-b: Umgebungswechsel (Hinweg ueber OTA, Rueckweg ueber reset) ──────

/** Ergebnis eines Aktivierungsversuchs — die UI macht daraus eine Meldung. */
export type EnvSwitchResult = 'switching' | 'no_update' | 'not_native' | 'error';

function reloadWindow(): void {
  try {
    if (typeof window !== 'undefined') window.location.reload();
  } catch {
    // Ohne window (Test/SSR) gibt es nichts neu zu laden.
  }
}

/**
 * Beta-Umgebung aktivieren: Wunsch + Einmal-Kennzeichen setzen und SOFORT einen
 * OTA-Lauf starten. Bei Erfolg kehrt runOtaUpdate nicht zurueck (set() laedt die
 * WebView neu).
 *
 * Scheitert der Lauf (Beta-Container unten, native Version zu alt, offline),
 * wird der Wunsch WIEDER GELOESCHT. Sonst liefe jeder kuenftige Start seinen
 * OTA-Check gegen einen toten Host und die Produktion bekaeme nie wieder ein
 * Update — der stille Dauerschaden waere schlimmer als der gescheiterte Wechsel.
 */
export async function activateBetaEnvironment(
  onProgress: OtaProgressCallback = () => undefined,
): Promise<EnvSwitchResult> {
  if (!Capacitor?.isNativePlatform?.()) return 'not_native';

  await writeEnvOverride('beta');
  await setEnvForce();

  let result: OtaResult;
  try {
    result = await runOtaUpdate(onProgress);
  } catch (error) {
    logOta('activateBetaEnvironment failed', error);
    await clearEnvOverride();
    await clearEnvForce();
    return 'error';
  }

  if (result.updated) return 'switching';

  await clearEnvOverride();
  await clearEnvForce();
  return result.error ? 'error' : 'no_update';
}

/**
 * Rueckweg in die Produktion. NICHT ueber einen OTA-Check: app.lorath.eu
 * antwortet fuer eine hoehere laufende Bundle-Nummer mit {"available":false}
 * (Downgrade-Sperre), das Geraet bliebe also dauerhaft auf dem Beta-JS gegen die
 * Prod-API haengen — das groesste Einzelrisiko des Vorhabens.
 *
 * Stattdessen: Wunsch loeschen, mit CapacitorUpdater.reset() auf das EINGEBAUTE
 * Store-Bundle zurueckfallen und neu laden. Der naechste regulaere Check zieht
 * von dort wieder auf den aktuellen Produktionsstand hoch.
 */
export async function returnToProduction(): Promise<EnvSwitchResult> {
  await clearEnvOverride();
  await clearEnvForce();

  if (!Capacitor?.isNativePlatform?.()) {
    reloadWindow();
    return 'not_native';
  }

  const updater = loadUpdaterPlugin();
  if (!updater || typeof updater.reset !== 'function') {
    // Ohne reset() bleibt nur der Neustart auf dem aktuellen Bundle — der
    // Wunsch ist geloescht, der naechste Check laeuft wieder gegen die
    // Produktion. Der Aufrufer meldet den Teilerfolg.
    reloadWindow();
    return 'error';
  }

  await withTimeout(updater.reset({ toLastSuccessful: false }), 30000, 'reset');
  reloadWindow();
  return 'switching';
}

// ─── Hilfen ──────────────────────────────────────────────────────────────────

/**
 * Wartet HÖCHSTENS `ms` auf das Promise. Timeout ODER Reject → null (nie werfen),
 * damit kein nativer Aufruf (notifyAppReady/getInfo/download) den OTA-Boot-Pfad
 * unendlich blockiert und den Nutzer im Overlay einsperrt.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        logOta(`${label} timed out after ${ms}ms`);
        resolve(null);
      }
    }, ms);
    promise.then(
      (value) => { if (!settled) { settled = true; clearTimeout(timer); resolve(value); } },
      (error) => { if (!settled) { settled = true; clearTimeout(timer); logOta(`${label} failed`, error); resolve(null); } },
    );
  });
}

/**
 * Server-geloggter Fortschritts-Beacon: GET /api/ota/check?…&beacon=STEP.
 * Fire-and-forget zur Ferndiagnose. Der Server erkennt `beacon` seit 09/2026
 * ausdruecklich, loggt den Schritt strukturiert und antwortet leer mit 204 —
 * vorher liefen die Beacons in die Allowlist-Validierung und wurden mit 400
 * abgewiesen (599/Tag). Der letzte Beacon vor Stille = die Haenger-Stelle on-device.
 */
function beacon(
  step: string,
  params: { apiBase?: string | null; platform?: string; versionCode?: number; bundle?: string; channel?: string },
): void {
  try {
    const base = (params.apiBase ?? '').replace(/\/+$/, '');
    if (!base) return;
    const q = new URLSearchParams({ beacon: step });
    // bundle = APP_VERSION IMMER mitschicken → in den Logs ist sofort sichtbar,
    // WELCHER Build den Beacon feuert (Diagnose über TestFlight-/APK-Versionen).
    q.set('bundle', params.bundle ?? APP_VERSION);
    if (params.platform) q.set('platform', params.platform);
    if (typeof params.versionCode === 'number') q.set('versionCode', String(params.versionCode));
    if (params.channel) q.set('channel', params.channel);
    void fetch(`${base}/api/ota/check?${q.toString()}`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    // Beacon ist best-effort.
  }
}

function clampPercent(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}

async function removeListener(handle: CapgoListenerHandle | null): Promise<void> {
  if (!handle) return;
  try {
    await handle.remove();
  } catch {
    // Listener-Cleanup ist best-effort.
  }
}

function logOta(message: string, error?: unknown): void {
  try {
    // Bewusst console.warn (kein throw) — Boot-Pfad darf nie hart abbrechen.
    console.warn(`[ota] ${message}`, error);
  } catch {
    // console kann in exotischen WebViews fehlen.
  }
}
